/**
 * Page-based utility functions for checklist management
 * Replaces step-based utilities with page-based organization
 */

/**
 * Organizes fsmAssets into page objects
 * @param {Array} fsmAssets - Array of asset checklist objects
 * @returns {Array} Array of page objects
 */
export const organizeAssetsIntoPages = (fsmAssets = []) => {
  if (!Array.isArray(fsmAssets) || fsmAssets.length === 0) {
    return [];
  }
  return fsmAssets.map((asset, index) => {
    const items = asset.template_config?.items || [];
    const completedItems = items.filter((item) => item.completed).length;
    return {
      id: `page-asset-${asset.work_order_line_item_id || index}`,
      pageNumber: index + 1,
      title: `Asset ${asset.asset_serial_number || `#${index + 1}`}`,
      assetSerialNumber: asset.asset_serial_number,
      workOrderLineItemId: asset.work_order_line_item_id,
      items: items,
      completed: items.length > 0 && items.every((item) => item.completed),
      totalItems:
        items.length - items.filter((item) => item.type === "checkbox").length,
      completedItems: completedItems,
      completedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });
};

/**
 * Calculates overall progress across all pages
 * @param {Array} pages - Array of page objects
 * @returns {Object} Progress information
 */
export const calculatePageProgress = (pages = []) => {
  if (!Array.isArray(pages) || pages.length === 0) {
    return {
      totalPages: 0,
      completedPages: 0,
      totalItems: 0,
      completedItems: 0,
      overallProgress: 0,
      pageProgress: [],
    };
  }
  console.log(pages);
  const totalPages = pages.length;
  const completedPages = pages.filter((page) => page.completed).length;
  const totalItems = pages.reduce((sum, page) => sum + page.totalItems, 0);
  const completedItems = pages.reduce(
    (sum, page) => sum + page.completedItems,
    0
  );
  const overallProgress =
    totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const pageProgress = pages.map((page) => ({
    pageNumber: page.pageNumber,
    progress:
      page.totalItems > 0
        ? Math.round((page.completedItems / page.totalItems) * 100)
        : 0,
    completed:
      Math.round((page.completedItems / page.totalItems) * 100) === 100
        ? true
        : false,
  }));

  console.log("Page Progress:", pageProgress);
  console.log("Overall Progress:", overallProgress);

  return {
    totalPages,
    completedPages,
    totalItems,
    completedItems,
    overallProgress,
    pageProgress,
  };
};

/**
 * Validates if a page can be considered complete
 */
export const validatePageCompletion = (page, requireAllItems = true) => {
  if (!page || !Array.isArray(page.items)) {
    return false;
  }

  if (requireAllItems) {
    return page.items.every(
      (item) => item.completed && item.type !== "checkbox"
    );
  }

  // If not requiring all items, consider page complete if at least 80% are done
  const completionRate = page.completedItems / page.totalItems;
  return completionRate >= 0.8;
};

/**
 * Gets the next incomplete page index
 */
export const getNextIncompletePage = (pages = [], currentPageIndex = 0) => {
  for (let i = currentPageIndex; i < pages.length; i++) {
    if (!pages[i].completed) {
      return i;
    }
  }
  return currentPageIndex;
};

/**
 * Checks if navigation to a specific page should be allowed
 */
export const canNavigateToPage = (
  pages = [],
  targetPageIndex,
  currentPageIndex,
  allowSkipping = false
) => {
  if (targetPageIndex < 0 || targetPageIndex >= pages.length) {
    return false;
  }

  // Always allow going back
  if (targetPageIndex <= currentPageIndex) {
    return true;
  }

  // If skipping is allowed, allow any forward navigation
  if (allowSkipping) {
    return true;
  }

  // Otherwise, only allow going to the next page if current is complete
  if (targetPageIndex === currentPageIndex + 1) {
    return pages[currentPageIndex].completed;
  }

  return false;
};

/**
 * Updates a page when an item within it changes
 */
export const updatePageItem = (page, itemId, updates) => {
  if (!page || !page.items) {
    return page;
  }

  const updatedItems = page.items.map((item) =>
    item.id === itemId ? { ...item, ...updates } : item
  );

  const completedItems = updatedItems.filter((item) => item.completed).length;
  const completed = updatedItems.every((item) => item.completed);

  return {
    ...page,
    fsmAssets: updatedItems,
    completedItems,
    completed,
    updatedAt: new Date().toISOString(),
  };
};

/**
 * Finds which page contains a specific item
 */
export const findItemInPages = (pages = [], itemId, workOrderLineItemId) => {
  const pageIndex = pages.findIndex(
    (p) => p.workOrderLineItemId === workOrderLineItemId
  );
  const items = pageIndex !== -1 ? pages[pageIndex].items : null;
  const itemIndex = items.findIndex((i) => i.id === itemId);
  return itemIndex !== -1
    ? { pageIndex, itemIndex, workOrderLineItemId }
    : null;
};
